// src/pages/SluzbyCennik.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PageContainer = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 50%, #C0C0C0 100%);
    padding-top: 150px;

`;

const Section = styled.section`
    padding: 96px 0;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(
                90deg,
                transparent 0%,
                rgba(153, 1, 71, 0.3) 50%,
                transparent 100%
        );
    }
`;

const BackgroundPattern = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.03;
    background-image: radial-gradient(circle at 25% 25%, #990147 2px, transparent 0),
    radial-gradient(circle at 75% 75%, #990147 2px, transparent 0);
    background-size: 60px 60px;
`;

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 2;

    @media (max-width: 768px) {
        padding: 0 1rem;
    }
`;

const HeaderWrapper = styled.div`
    text-align: center;
    margin-bottom: 4rem;
`;

const HeaderTag = styled(motion.span)`
    display: inline-block;
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    color: white;
    padding: 0.6rem 1.6rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.5px;
    margin-bottom: 1.2rem;
    box-shadow: 0 6px 20px rgba(153, 1, 71, 0.2);
`;

const SectionTitle = styled(motion.h2)`
    font-size: 2.8rem;
    font-weight: 800;
    background: linear-gradient(135deg, #333333 0%, #990147 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 1.2rem;
    line-height: 1.1;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const SectionSubtitle = styled(motion.p)`
    font-size: 1rem;
    color: #555555;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.5;
    font-weight: 400;
`;

// Pricing Cards (reused from existing CennikSection)
const PlansGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
    gap: 2.4rem;
    margin-bottom: 3.2rem;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const PlanCard = styled(motion.article)`
    background: rgba(255, 255, 255, 0.95);
    border-radius: 20px;
    padding: 2.4rem;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.8);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    backdrop-filter: blur(10px);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    }

    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 24px 48px rgba(153, 1, 71, 0.15);
    }
`;

const PlanHeader = styled.div`
    margin-bottom: 2rem;
    text-align: center;
`;

const PlanName = styled.h3`
    font-size: 1.6rem;
    font-weight: 700;
    background: linear-gradient(135deg, #333333 0%, #990147 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 0.8rem;
`;

const PlanDescription = styled.p`
    color: #555555;
    font-size: 0.9rem;
    line-height: 1.5;
`;

const PriceBadge = styled(motion.div)`
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    color: white;
    padding: 0.8rem 1.6rem;
    border-radius: 40px;
    font-weight: 700;
    font-size: 1.2rem;
    display: inline-block;
    margin-bottom: 1.6rem;
    box-shadow: 0 8px 24px rgba(153, 1, 71, 0.3);
`;

const ServicesList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 2rem 0;
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
`;

const ServiceItem = styled(motion.li)`
    display: flex;
    align-items: flex-start;
    gap: 0.8rem;
    font-size: 0.9rem;
    color: #555555;
    line-height: 1.4;
    padding: 0.8rem;
    background: rgba(248, 248, 248, 0.8);
    border-radius: 10px;
    border-left: 2px solid #990147;

    .check {
        flex-shrink: 0;
        margin-top: 0.1rem;
        color: #990147;
        width: 18px;
        height: 18px;
        filter: drop-shadow(0 1px 3px rgba(153, 1, 71, 0.2));
    }
`;

const ServicePrice = styled.span`
    font-weight: 700;
    color: #990147;
    margin-left: auto;
    padding-left: 0.8rem;
    font-size: 0.95rem;
`;

const ServiceDescription = styled.p`
    font-size: 0.8rem;
    color: #777777;
    margin-top: 0.4rem;
    line-height: 1.3;
    font-style: italic;
`;

// New Components for Additional Services
const ServicesGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: start;
    margin: 4rem 0;

    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 3rem;
    }
`;

const ServicesColumn = styled.div`
    position: relative;
`;

const ServicesCategory = styled(motion.div)`
    background: rgba(255, 255, 255, 0.9);
    border-radius: 20px;
    padding: 2.5rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.8);
    position: relative;
    overflow: hidden;
    backdrop-filter: blur(10px);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 4px;
        background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    }

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 20px 40px rgba(153, 1, 71, 0.15);
    }

    transition: all 0.3s ease;
`;

const CategoryTitle = styled.h3`
    font-size: 1.5rem;
    font-weight: 700;
    background: linear-gradient(135deg, #333333 0%, #990147 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.8rem;

    &::before {
        content: '';
        width: 6px;
        height: 6px;
        background: #990147;
        border-radius: 50%;
    }
`;

const ServiceList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const ServiceListItem = styled(motion.li)`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: 0.8rem 0;
    border-bottom: 1px solid rgba(153, 1, 71, 0.1);
    color: #555555;
    font-size: 0.95rem;

    &:last-child {
        border-bottom: none;
    }

    .bullet {
        color: #990147;
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }
`;

// Doctor Section
const DoctorSection = styled.div`
    margin: 4rem 0;
`;

const DoctorGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1.2fr;
    gap: 4rem;
    align-items: center;

    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 3rem;
    }
`;

const DoctorImageWrapper = styled(motion.div)`
    position: relative;
    height: 500px;
    border-radius: 25px;
    overflow: hidden;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.8) inset;
`;

const DoctorImage = styled(motion.img)`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const DoctorInfo = styled.div`
    position: relative;

    h3 {
        font-size: 2.2rem;
        font-weight: 800;
        margin-bottom: 1.5rem;
        background: linear-gradient(135deg, #333333 0%, #990147 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        line-height: 1.1;
    }
`;

const DoctorDescription = styled.div`
    p {
        font-size: 1.1rem;
        color: #555555;
        line-height: 1.7;
        margin-bottom: 1.5rem;
        font-weight: 400;

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const ImportantNote = styled(motion.div)`
    background: linear-gradient(135deg, rgba(153, 1, 71, 0.95) 0%, rgba(204, 51, 102, 0.95) 100%);
    color: white;
    padding: 2.4rem;
    border-radius: 16px;
    text-align: center;
    margin-top: 3.2rem;
    box-shadow: 0 16px 32px rgba(153, 1, 71, 0.3);
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(
                circle at 20% 80%,
                rgba(255, 255, 255, 0.1) 0%,
                transparent 50%
        );
        pointer-events: none;
    }
`;

const NoteText = styled.p`
    color: #FFFFFF;
    font-size: 0.95rem;
    line-height: 1.5;
    margin: 0;
    font-weight: 500;
    position: relative;
    z-index: 2;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SluzbyCennik: React.FC = () => {


    const providedServices = [
        "Vstupné psychiatrické vyšetrenie",
        "Pravidelná ambulantná kontrola",
        "Mimoriadna ambulantná kontrola",
        "Telefonická konzultácia",
        "E-mailová konzultácia",
        "Vyhotovenie lekárskej správy",
        "Vyšetrenie pre potreby polície",
        "Sprostredkovanie špecializovaného vyšetrenia",
        "Krízová intervencia",
        "Vyžiadaná edukácia",
        "Poradenstvo v oblasti výživy a životného štýlu"
    ];

    const additionalServices = [
        "Telefonická a emailová konzultácia",
        "Vyšetrenie vo večerných hodinách, počas víkendov, v domácom prostredí",
        "Sprostredkovanie ďalších nadväzujúcich vyšetrení a liečebných postupov (laboratórna a prístrojová diagnostika, ďalšie vyšetrenia špecialistami, psychodiagnostika, psychoterapia)"
    ];

    return (
        <PageContainer>
            <Section id="sluzby-cennik">
                <BackgroundPattern />
                <Container>
                    <HeaderWrapper>
                        <HeaderTag
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            Služby & Cenník
                        </HeaderTag>
                        <SectionTitle
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            viewport={{ once: true }}
                        >
                            Komplexná psychiatrická starostlivosť
                        </SectionTitle>
                        <SectionSubtitle
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            viewport={{ once: true }}
                        >
                            Poskytujeme široké spektrum psychiatrických služieb s dôrazom na individualizovaný prístup a maximálnu diskrétnosť
                        </SectionSubtitle>
                    </HeaderWrapper>

                    {/* Pricing Cards */}
                    <PlansGrid>
                        <PlanCard
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            <PlanHeader>
                                <PlanName>Základné služby</PlanName>
                                <PlanDescription>
                                    Komplexné psychiatrické vyšetrenia a konzultácie
                                </PlanDescription>
                            </PlanHeader>

                            <PriceBadge
                                initial={{ scale: 0 }}
                                whileInView={{ scale: 1 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                                viewport={{ once: true }}
                            >
                                Od 10€
                            </PriceBadge>

                            <ServicesList>
                                <ServiceItem
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: 0.1 }}
                                    viewport={{ once: true }}
                                >
                                    <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        Prvé vstupné psychiatrické vyšetrenie
                                        <ServiceDescription>
                                            Anamnéza, diagnostika, liečebný plán, edukácia
                                        </ServiceDescription>
                                    </div>
                                    <ServicePrice>250€</ServicePrice>
                                </ServiceItem>

                                <ServiceItem
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: 0.2 }}
                                    viewport={{ once: true }}
                                >
                                    <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        Jednorazové psychiatrické vyšetrenie
                                        <ServiceDescription>
                                            Pre sociálne, posudkové alebo úradné účely
                                        </ServiceDescription>
                                    </div>
                                    <ServicePrice>250€</ServicePrice>
                                </ServiceItem>

                                <ServiceItem
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: 0.3 }}
                                    viewport={{ once: true }}
                                >
                                    <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        Kontrolné vyšetrenie - rozšírené
                                        <ServiceDescription>
                                            Do 45 minút vrátane psychoterapie (možné online)
                                        </ServiceDescription>
                                    </div>
                                    <ServicePrice>80€</ServicePrice>
                                </ServiceItem>

                                <ServiceItem
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: 0.4 }}
                                    viewport={{ once: true }}
                                >
                                    <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        Kontrolné vyšetrenie - základné
                                        <ServiceDescription>
                                            Do 10 minút, aktualizácia liečby (možné online)
                                        </ServiceDescription>
                                    </div>
                                    <ServicePrice>30€</ServicePrice>
                                </ServiceItem>
                            </ServicesList>

                        </PlanCard>

                        <PlanCard
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            viewport={{ once: true }}
                        >
                            <PlanHeader>
                                <PlanName>Špeciálne služby</PlanName>
                                <PlanDescription>
                                    Doplnkové a nadštandardné služby podľa vašich potrieb
                                </PlanDescription>
                            </PlanHeader>

                            <PriceBadge
                                initial={{ scale: 0 }}
                                whileInView={{ scale: 1 }}
                                transition={{ duration: 0.5, delay: 0.4 }}
                                viewport={{ once: true }}
                            >
                                Od 10€
                            </PriceBadge>

                            <ServicesList>
                                <ServiceItem
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: 0.2 }}
                                    viewport={{ once: true }}
                                >
                                    <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        Vypísanie receptov a základná kontrola
                                        <ServiceDescription>
                                            V rámci zastupovania
                                        </ServiceDescription>
                                    </div>
                                    <ServicePrice>10€</ServicePrice>
                                </ServiceItem>

                                <ServiceItem
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: 0.3 }}
                                    viewport={{ once: true }}
                                >
                                    <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        Podrobná lekárska správa
                                        <ServiceDescription>
                                            Pre Sociálnu poisťovňu, úradné účely, cestovanie
                                        </ServiceDescription>
                                    </div>
                                    <ServicePrice>24.80€</ServicePrice>
                                </ServiceItem>

                                <ServiceItem
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: 0.4 }}
                                    viewport={{ once: true }}
                                >
                                    <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        Konzultácia s príbuznými
                                    </div>
                                    <ServicePrice>40€</ServicePrice>
                                </ServiceItem>

                                <ServiceItem
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: 0.5 }}
                                    viewport={{ once: true }}
                                >
                                    <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        Ročný paušálny poplatok
                                        <ServiceDescription>
                                            Recepty na požiadanie, mailová a SMS komunikácia
                                        </ServiceDescription>
                                    </div>
                                    <ServicePrice>30€/rok</ServicePrice>
                                </ServiceItem>

                                <ServiceItem
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: 0.6 }}
                                    viewport={{ once: true }}
                                >
                                    <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        Vyšetrenie mimo ordinačných hodín
                                        <ServiceDescription>
                                            Vo večerných hodinách, víkendy, v domácom prostredí
                                        </ServiceDescription>
                                    </div>
                                    <ServicePrice>+100%</ServicePrice>
                                </ServiceItem>

                                <ServiceItem
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: 0.7 }}
                                    viewport={{ once: true }}
                                >
                                    <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        Kopírovanie
                                        <ServiceDescription>
                                            Jedna strana A4
                                        </ServiceDescription>
                                    </div>
                                    <ServicePrice>0.40€</ServicePrice>
                                </ServiceItem>
                            </ServicesList>

                        </PlanCard>
                    </PlansGrid>

                    {/* Additional Services Section */}
                    <ServicesGrid>
                        <ServicesColumn>
                            <ServicesCategory
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -5 }}
                            >
                                <CategoryTitle>Poskytované výkony</CategoryTitle>
                                <ServiceList>
                                    {providedServices.map((service, index) => (
                                        <ServiceListItem
                                            key={index}
                                            initial={{ opacity: 0, x: -20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.5, delay: index * 0.1 }}
                                            viewport={{ once: true }}
                                        >
                                            <svg className="bullet" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                            {service}
                                        </ServiceListItem>
                                    ))}
                                </ServiceList>
                            </ServicesCategory>
                        </ServicesColumn>

                        <ServicesColumn>
                            <ServicesCategory
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -5 }}
                            >
                                <CategoryTitle>Doplnkové služby</CategoryTitle>
                                <ServiceList>
                                    {additionalServices.map((service, index) => (
                                        <ServiceListItem
                                            key={index}
                                            initial={{ opacity: 0, x: 20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.5, delay: index * 0.1 }}
                                            viewport={{ once: true }}
                                        >
                                            <svg className="bullet" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                            {service}
                                        </ServiceListItem>
                                    ))}
                                </ServiceList>
                            </ServicesCategory>
                        </ServicesColumn>
                    </ServicesGrid>

                    {/* Doctor Section */}
                    <DoctorSection>
                        <DoctorGrid>
                            <DoctorImageWrapper
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                                viewport={{ once: true }}
                            >
                                <DoctorImage
                                    src="/sonaCennik.jpg"
                                    alt="MUDr. Soňa Belanská"
                                    initial={{ scale: 1.1 }}
                                    whileInView={{ scale: 1 }}
                                    transition={{ duration: 1.2 }}
                                    viewport={{ once: true }}
                                />
                            </DoctorImageWrapper>

                            <DoctorInfo>
                                <motion.h3
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8 }}
                                    viewport={{ once: true }}
                                >
                                    O lekárovi
                                </motion.h3>

                                <DoctorDescription>
                                    <motion.p
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.8, delay: 0.1 }}
                                        viewport={{ once: true }}
                                    >
                                        MUDr. Soňa Belanská je skúsená psychiatrička s dlhoročnou praxou v oblasti duševného zdravia. Štúdium na Jesseniovej lekárskej fakulte UK v Martine ukončila s vyznamenaním a následne získala bohaté skúsenosti v rôznych psychiatrických zariadeniach.
                                    </motion.p>

                                    <motion.p
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.8, delay: 0.2 }}
                                        viewport={{ once: true }}
                                    >
                                        V roku 2009 absolvovala atestáciu v odbore psychiatria a ukončila 4-ročné vzdelávanie v Kognitívno-behaviorálnej psychoterapii. Je zapísaná do Zoznamu psychoterapeutov SR a pôsobí ako predsedníčka Dozornej rady Gerontopsychiatrickej sekcie Slovenskej psychiatrickej spoločnosti.
                                    </motion.p>

                                    <motion.p
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.8, delay: 0.3 }}
                                        viewport={{ once: true }}
                                    >
                                        Okrem psychiatrie sa venuje aj tréningu a výživovému poradenstvu, čo jej umožňuje komplexný pohľad na zdravie pacientov. Ako expertku ste ju mohli vidieť v slovenských médiách, kde prispieva k zvýšeniu povedomia o duševnom zdraví.
                                    </motion.p>
                                </DoctorDescription>
                            </DoctorInfo>
                        </DoctorGrid>
                    </DoctorSection>

                    <ImportantNote
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        viewport={{ once: true }}
                    >
                        <NoteText>
                            💡 Dôležité: Ak sa nemôžete dostaviť na vyšetrenie, je nutné sa z termínu odhlásiť a termín uvoľniť pre iného pacienta.
                            Odhlásenie z termínu minimálne 24 hodín vopred je bezplatné. Neskoré odhlásenie alebo nepríchod je spoplatnený storno poplatkom 40€.
                        </NoteText>
                    </ImportantNote>
                </Container>
            </Section>
        </PageContainer>
    );
};

export default SluzbyCennik;